import type { FormSection } from '../../interfaces/form.interface';
import { ReactiveSectionFactory, type ReactiveSectionFactoryConfig } from './reactive-field-factory';

const DEFAULT_IDENTIFICATION_SECTION_NAME = 'Identificación';
const DEFAULT_LOCATION_SECTION_NAME = 'Localización';
const DEFAULT_ADMINISTRATIVE_SECTION_NAME = 'Datos administrativos';
const DEFAULT_OBSERVATIONS_SECTION_NAME = 'Observaciones';
const DEFAULT_FILES_SECTION_NAME = 'Ficheros / documentos';

export type ReactiveDomainSectionFactoryConfig<TModel> = Omit<ReactiveSectionFactoryConfig<TModel>, 'name'> & {
    readonly name?: string;
};

/** Builds the standard identification section used by CRUD and business forms. */
export class ReactiveIdentificacionSectionFactory {
    public static create<TModel>(config: ReactiveDomainSectionFactoryConfig<TModel>): FormSection<TModel> {
        return ReactiveSectionFactory.create<TModel>({
            ...config,
            name: config.name ?? DEFAULT_IDENTIFICATION_SECTION_NAME,
        });
    }
}

/** Builds the standard location section used by territorial and address-based forms. */
export class ReactiveLocalizacionSectionFactory {
    public static create<TModel>(config: ReactiveDomainSectionFactoryConfig<TModel>): FormSection<TModel> {
        return ReactiveSectionFactory.create<TModel>({
            ...config,
            name: config.name ?? DEFAULT_LOCATION_SECTION_NAME,
        });
    }
}

/** Builds the standard administrative section used for ownership, status and technical data. */
export class ReactiveDatosAdministrativosSectionFactory {
    public static create<TModel>(config: ReactiveDomainSectionFactoryConfig<TModel>): FormSection<TModel> {
        return ReactiveSectionFactory.create<TModel>({
            ...config,
            name: config.name ?? DEFAULT_ADMINISTRATIVE_SECTION_NAME,
        });
    }
}

/** Builds the standard observations section for free-text notes and comments. */
export class ReactiveObservacionesSectionFactory {
    public static create<TModel>(config: ReactiveDomainSectionFactoryConfig<TModel>): FormSection<TModel> {
        return ReactiveSectionFactory.create<TModel>({
            ...config,
            name: config.name ?? DEFAULT_OBSERVATIONS_SECTION_NAME,
        });
    }
}

/** Builds the standard files section reserved for managed documents and uploads. */
export class ReactiveFicherosSectionFactory {
    public static create<TModel>(config: ReactiveDomainSectionFactoryConfig<TModel>): FormSection<TModel> {
        return ReactiveSectionFactory.create<TModel>({
            ...config,
            name: config.name ?? DEFAULT_FILES_SECTION_NAME,
        });
    }
}

/** Convenience facade for callers that prefer a single import for domain section factories. */
export class ReactiveDomainSectionFactory {
    public static identificacion<TModel>(config: ReactiveDomainSectionFactoryConfig<TModel>): FormSection<TModel> {
        return ReactiveIdentificacionSectionFactory.create(config);
    }

    public static localizacion<TModel>(config: ReactiveDomainSectionFactoryConfig<TModel>): FormSection<TModel> {
        return ReactiveLocalizacionSectionFactory.create(config);
    }

    public static datosAdministrativos<TModel>(config: ReactiveDomainSectionFactoryConfig<TModel>): FormSection<TModel> {
        return ReactiveDatosAdministrativosSectionFactory.create(config);
    }

    public static observaciones<TModel>(config: ReactiveDomainSectionFactoryConfig<TModel>): FormSection<TModel> {
        return ReactiveObservacionesSectionFactory.create(config);
    }

    public static ficheros<TModel>(config: ReactiveDomainSectionFactoryConfig<TModel>): FormSection<TModel> {
        return ReactiveFicherosSectionFactory.create(config);
    }
}
